import React, { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { useLocation } from "react-router-dom";
import PageWrapper from "../pageWrapper";
import { useNavigate } from "react-router-dom";
import { fetchWithTimeout } from "../../hooks/useFetchWithTimeout";

//CSS
import "./noteCreationPage.css";
import "../0PageStyles.css";

//Components
import InputField from "../../Components/Other Components/inputField";
import InputGroup from "../../Components/Other Components/inputGroup";
import Button from "../../Components/Other Components/button";
import ClientPicker from "../../Components/Client Components/clientPicker";
import WarehousePicker from "../../Components/Warehouse Components/warehousePicker";
import PopupConfirmation from "../../Components/Other Components/popupConfirmation";
import PageLoader from "../../Components/Other Components/pageLoader";
import MandatoryExplanation from "../../Components/Other Components/mandatoryExplanation";
import BarcodeDisplayComponent from "../../Components/Other Components/BarcodeDisplayComponent";

//Functions and hooks
import { CreateNote, GetNextNoteID } from "../../BackendAccess/TrafficHooks";
import ItemPicker from "../../Components/Item Components/itemPicker";
import { useNotification } from "../../Contexts/NotificationContext";
import { useErrorHandler } from "../../hooks/errorHandler";
import useNarrowMode from "../../hooks/useNarrowModeChecker";
import { formatCurrency, useSettings } from "../../Contexts/SettingsContext";
import { useTranslation } from "react-i18next";
import { usePopup } from "../../Contexts/PopupContext";

export default function NoteCreationPage() {
  const NoteTypes = {
    DELIVERY: "Delivery",
    RECEIVED_GOODS: "Received goods",
    SALE: "Sale",
  };
  const location = useLocation();
  const initSelectedWarehouse = location?.state?.WarehouseID || null;
  const [ID, setID] = useState();

  const [Clients, setClients] = useState([]);
  const [Warehouses, setWarehouses] = useState([]);
  const [Discount, setDiscount] = useState(0);
  const [NoteType, setNoteType] = useState("Received goods");
  const [Comment, setComment] = useState("");
  const [totalPrice, setTotalPrice] = useState(0);
  const [totalPriceWithDiscount, setTotalPriceWithDiscount] =
    useState(totalPrice);

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [pageError, setPageError] = useState(null);

  const [selectionItemAmounts, setSelectionItemAmounts] = useState(new Map());
  const [showConfirmation, setShowConfirmation] = useState(false);
  const navigate = useNavigate();

  const [createBtnDisabled, setCreateBtnDisabled] = useState(false);

  const { showNotification } = useNotification();
  useErrorHandler(error, showNotification, setPageError);
  const isNarrow = useNarrowMode();

  const { companySettings, refreshCompanySettings } = useSettings();

  const { t } = useTranslation();

  const { showPopup, removePopup } = usePopup();

  const ShowCreatePopup = () => {
    const key = Date.now();
    showPopup(
      <PopupConfirmation
        message={
          <p>
            <span>
              {t("Are you sure you want to create this note?", {
                ns: "traffic",
              })}
            </span>
          </p>
        }
        onConfirm={() => OnCreate(key)}
        onCancel={() => onCacelCreate(key)}
      />,

      key
    );
  };

  useEffect(() => {
    const fetchInitialValues = async () => {
      setLoading(true);

      try {
        await refreshCompanySettings();

        const [data, error] = await fetchWithTimeout(GetNextNoteID);
        if (error) {
          error.isPageError = true;
          setError(error);
        } else {
          setID(data);
        }
      } catch (err) {
        setError({
          err: t("server.Server error.", { ns: "error" }),
          errCode: 500,
        });
      } finally {
        setLoading(false);
      }
    };

    fetchInitialValues();
  }, []);
  useEffect(() => {
    if (initSelectedWarehouse) {
      onSelectedWarehousesChanged([initSelectedWarehouse]);
    }
  }, [initSelectedWarehouse]);

  useEffect(() => {
    const newDisPrice = totalPrice - (Discount / 100) * totalPrice;
    const roundedNewPrice = parseFloat(newDisPrice.toFixed(2));
    setTotalPriceWithDiscount(roundedNewPrice);
  }, [Discount, totalPrice]);

  const handleIDChange = (e) => {
    const value = e;

    setID(value);
  };
  const handleNoteTypeChange = (e) => {
    const value = e;
    if (value === NoteType) return;
    setNoteType(value);
  };

  const handleDiscountChange = (e) => {
    const value = parseFloat(e);
    if (e === "") {
      setDiscount(0);
    } else if (value >= 0 && value <= 100) {
      setDiscount(value);
    } else {
      setError({
        err: t("validation.Discount must be between 0 and 100.", {
          ns: "error",
        }),
        errCode: 400,
      });
    }
  };

  const updateTotalPrice = (amount) => {
    console.log("Updating total price", amount);
    setTotalPrice(amount);
  };

  const OnCreate = async (key) => {
    if (NoteType === NoteTypes.DELIVERY)
      showNotification(
        t("Creating delivery note...", { ns: "traffic" }),
        "info"
      );
    else if (NoteType === NoteTypes.RECEIVED_GOODS)
      showNotification(
        t("Creating received goods note...", { ns: "traffic" }),
        "info"
      );
    else if (NoteType === NoteTypes.SALE)
      showNotification(t("Creating sale note...", { ns: "traffic" }), "info");
    setCreateBtnDisabled(true);
    try {
      const NoteID = ID;
      const Type = NoteType;
      // Filter out items with amount 0
      const filteredItems = Array.from(selectionItemAmounts.entries()).filter(
        ([, amount]) => amount > 0
      );
      const Items = filteredItems.map(([item]) => item);
      const Amounts = filteredItems.map(([, amount]) => amount);
      const ItemsString = Items.join(",");
      const AmountsString = Amounts.join(",");
      const Warehouse = Warehouses[0];
      const Client = Clients[0];
      const DiscountValue = Discount;
      const TotalPrice = totalPrice;

      const [data, error] = await CreateNote({
        ID: NoteID,
        Type: Type,
        Items: ItemsString,
        Amounts: AmountsString,
        WarehouseID: Warehouse,
        ClientID: Client,
        Price: TotalPrice,
        Discount: DiscountValue,
        Comment: Comment,
      });

      if (!error) {
        if (NoteType === NoteTypes.DELIVERY)
          showNotification(
            t("Delivery note created successfully!", { ns: "traffic" }),
            "success"
          );
        else if (NoteType === NoteTypes.RECEIVED_GOODS)
          showNotification(
            t("Received goods note created successfully!", { ns: "traffic" }),
            "success"
          );
        else if (NoteType === NoteTypes.SALE)
          showNotification(
            t("Sale note created successfully!", { ns: "traffic" }),
            "success"
          );

        navigate(-1);
      } else {
        setError(error);
        return;
      }
    } catch (err) {
      setError({
        err: t("server.Server error.", { ns: "error" }),
        errCode: 500,
      });
    } finally {
      setCreateBtnDisabled(false);
      removePopup(key);
    }
  };
  const onCacelCreate = (key) => {
    showNotification(t("Note creation canceled.", { ns: "traffic" }), "info");
    removePopup(key);
  };
  const onCreateBtnClick = () => {
    ShowCreatePopup();
  };
  const onSelectionItemAmountsChange = (amounts) => {
    setSelectionItemAmounts(amounts);
  };

  const clickOnCancel = () => {
    navigate(-1);
  };

  const onSelectedClientsChanged = (clients) => {
    setClients(clients);
  };
  const onSelectedWarehousesChanged = (warehouses) => {
    setWarehouses(warehouses);
  };

  const [code, setCode] = useState(0);
  const makeQR = (value) => {
    setCode(value);
  };

  const tutorialSteps = [
    {
      target: null,
      title: t("Creating a traffic note", { ns: "tutorial" }),
      message: t(
        "This page creates a new traffic note - a record of stock moving in or out of a warehouse. There are three types: received goods, delivery, and sale.",
        { ns: "tutorial" }
      ),
    },
    {
      target: "note-creation-type",
      title: t("Note type", { ns: "tutorial" }),
      message: t(
        "Choose what kind of note this is. Received goods adds stock from a supplier, Delivery sends stock out to a buyer, and Sale removes stock without a specific client.",
        { ns: "tutorial" }
      ),
    },
    {
      target: "note-creation-discount",
      title: t("Discount", { ns: "tutorial" }),
      message: t(
        "Optional. Apply a percentage discount to the note's total price.",
        { ns: "tutorial" }
      ),
    },
    {
      target: "note-creation-client",
      title: t("Client", { ns: "tutorial" }),
      message: t(
        "For received goods and deliveries, pick the supplier or buyer this note is for. Sale notes don't need a client.",
        { ns: "tutorial" }
      ),
    },
    {
      target: "note-creation-warehouse",
      title: t("Select warehouse", { ns: "tutorial" }),
      message: t(
        "Pick which warehouse this note affects - stock will be added to or removed from here.",
        { ns: "tutorial" }
      ),
    },
    {
      target: ".PageItemViewContainer",
      title: t("Select items", { ns: "tutorial" }),
      message: t(
        "Once a warehouse (and client, if needed) is picked, items appear here. Use the + and - to set how much of each item this note covers.",
        { ns: "tutorial" }
      ),
    },
    {
      target: "note-creation-comment",
      title: t("Comment", { ns: "tutorial" }),
      message: t(
        "Optional. Add a free-text note for your own reference, like delivery instructions or a reminder.",
        { ns: "tutorial" }
      ),
    },
    {
      target: "note-creation-create-btn",
      title: t("Save the note", { ns: "tutorial" }),
      message: t(
        "Once everything looks right, click here to create the note. Stock is adjusted immediately and the note is added to your Traffic list.",
        { ns: "tutorial" }
      ),
    },
  ];

  return (
    <PageWrapper tutorialId="noteCreation" tutorialSteps={tutorialSteps}>
      <PageLoader error={pageError} loading={loading}>
        <div className="page-content">
          <div className="box-container item-creation">
            <InputGroup className="empty-container item-creation input">
              <InputField
                placeholder={t("Example: 1001")}
                label={t("ID") + ":"}
                value={ID}
                onChange={handleIDChange}
                readOnly={true}
                mandatory={true}
              />
              <InputField
                placeholder={t("Example: Delivery note", { ns: "traffic" })}
                label={t("Note type") + ":"}
                value={NoteType}
                onChange={handleNoteTypeChange}
                type="dropdown"
                options={[
                  {
                    value: NoteTypes.RECEIVED_GOODS,
                    label: t(NoteTypes.RECEIVED_GOODS),
                  },
                  { value: NoteTypes.DELIVERY, label: t(NoteTypes.DELIVERY) },
                  { value: NoteTypes.SALE, label: t(NoteTypes.SALE) },
                ]}
                mandatory={true}
                tutorialId="note-creation-type"
              />
              <InputField
                placeholder={t("Example: 10", { ns: "traffic" })}
                label={t("Discount (in %)", { ns: "traffic" }) + ":"}
                value={Discount}
                onChange={handleDiscountChange}
                tutorialId="note-creation-discount"
              />
              <MandatoryExplanation />
            </InputGroup>

            {NoteType === NoteTypes.DELIVERY ? (
              <div data-tutorial-id="note-creation-client">
                <ClientPicker
                  title={t("Select buyer", { ns: "traffic" })}
                  onChange={onSelectedClientsChanged}
                  typesToShow={["Buyer"]}
                  PickedClients={Clients}
                  className={isNarrow ? "mt-2" : "ml-5"}
                ></ClientPicker>
              </div>
            ) : NoteType === NoteTypes.RECEIVED_GOODS ? (
              <div data-tutorial-id="note-creation-client">
                <ClientPicker
                  title={t("Select supplier/manufacturer", { ns: "traffic" })}
                  onChange={onSelectedClientsChanged}
                  typesToShow={["Manufacturer", "Supplier"]}
                  PickedClients={Clients}
                  singleSelect={true}
                  className={isNarrow ? "mt-2" : "ml-5"}
                ></ClientPicker>
              </div>
            ) : null}
          </div>
          <div className="PageWarehousePickerContainer" data-tutorial-id="note-creation-warehouse">
            <WarehousePicker
              title={t("Select warehouse")}
              onChange={onSelectedWarehousesChanged}
              singleSelect={true}
              initWarehouses={
                initSelectedWarehouse ? [initSelectedWarehouse] : []
              }
            ></WarehousePicker>
          </div>
          <div className="PageItemViewContainer">
            <ItemPicker
              Title="Select items"
              bShowAll={false}
              noteType={NoteType}
              ClientID={
                NoteType === NoteTypes.DELIVERY || NoteType === NoteTypes.SALE
                  ? -1
                  : Clients[0]
                  ? Clients[0]
                  : -2
              }
              onQRCodeValueChange={makeQR}
              bEnableEdit={false}
              WarehouseID={Warehouses[0] ? Warehouses[0] : -1}
              updateTotalPrice={updateTotalPrice}
              onSelectionItemAmountsChange={onSelectionItemAmountsChange}
              bDisabled={
                Warehouses.length === 0 ||
                (Clients.length === 0 && NoteType !== NoteTypes.SALE)
              }
              bLimitAmount={NoteType === NoteTypes.DELIVERY}
            />
          </div>

          <div className="empty-container ninety-perc">
            <InputField
              className="mb-2"
              placeholder={t("Total price", { ns: "traffic" })}
              label={t("Total price", { ns: "traffic" }) + ":"}
              value={formatCurrency(totalPrice, companySettings.currency)}
              inputMargins={["0", "0", "1rem", "0"]}
              labelWidth={"7rem"}
              readOnly={true}
            ></InputField>
            <InputField
              placeholder={t("Total price with discount", { ns: "traffic" })}
              label={t("Total price with discount", { ns: "traffic" }) + ":"}
              value={formatCurrency(
                totalPriceWithDiscount,
                companySettings.currency
              )}
              labelWidth={"7rem"}
              readOnly={true}
            ></InputField>
          </div>
          <div className="empty-container ninety-perc">
            <InputField
              className="mb-2"
              placeholder={t("Delivered successfully!", { ns: "traffic" })}
              label={t("Comment") + ":"}
              value={Comment}
              inputMargins={["0", "0", "1rem", "0"]}
              labelWidth={"7rem"}
              onChange={(e) => {
                setComment(e);
              }}
              largeField={true}
              tutorialId="note-creation-comment"
            ></InputField>
          </div>
          <div className="btn-container">
            <Button
              btnMargin={"btn-default"}
              btnStyle="btn-positive"
              onClick={onCreateBtnClick}
              disabled={createBtnDisabled}
              acceptEnter={true}
              icon={"create"}
              tutorialId="note-creation-create-btn"
            >
              {t("Create")}
            </Button>

            <BarcodeDisplayComponent value={code} />
            <Button
              btnMargin={"btn-default"}
              btnStyle="btn-primary"
              onClick={clickOnCancel}
            >
              {t("Cancel")}
            </Button>
          </div>
        </div>
      </PageLoader>
    </PageWrapper>
  );
}
